import { SerializedError } from "@reduxjs/toolkit"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import {
  ApiKey,
  BillingFrequency,
  BillingPlanUsage,
  BlobInfo,
  DocShareMode,
  DocShareResponse,
  Document,
  DocumentMutationResult,
  EgressStats,
  IrohError,
  NodeStatus,
  Project,
  ProjectSettings,
  ResetPasswordRequest,
  ResetPasswordResponse,
  StripeInvoice,
  StripeInvoiceSettings,
  StripePaymentMethod,
  StripeSubscription,
  TeamMember,
  UploadBlobRequest,
  User,
  UserProjects,
  UserTeam,
  UserUpdates,
  Uuid,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from "../types"

const LOCAL_DOMAIN =
  import.meta.env.VITE_APP_IROH_ANCHOR_BASE_DOMAIN || "localhost"
export const IROH_ANCHOR_API_PREFIX =
  ~~window.location.hostname.indexOf("local") >= 0
    ? `http://${LOCAL_DOMAIN}:8000/`
    : `https://api.${window.location.hostname}/`

interface Pagination {
  offset: number
  limit: number
}

export type UserProjectParams = {
  username: string
  project: string
}

type DocShareRequest = {
  mode: DocShareMode
  docId: string
}

export type ApiKeyCreateResponse = {
  key_id: string
  name: string
  created_at: number
  secret: string
}

// Define a service using a base URL and expected endpoints
export const api = createApi({
  reducerPath: "irohApi",
  baseQuery: fetchBaseQuery({ baseUrl: IROH_ANCHOR_API_PREFIX }),
  endpoints: (build) => ({
    nodeStatus: build.query<NodeStatus, void>({
      query: () => ({
        url: "node/status",
        mode: "cors",
        credentials: "include",
      }),
    }),

    user: build.query<User, { username: string }>({
      query: ({ username }) => ({
        url: `users/${username}`,
        mode: "cors",
        credentials: "include",
      }),
    }),

    teamCreate: build.mutation<User, { username: string }>({
      query: ({ username }) => ({
        url: "teams",
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { members: [], username },
      }),
    }),

    teamMembers: build.query<TeamMember[], { teamname: string }>({
      query: ({ teamname }) => ({
        url: `teams/${teamname}/members`,
        method: "GET",
        mode: "cors",
        credentials: "include",
      }),
    }),
    addTeamMember: build.mutation<void, { teamname: string; user_id: Uuid }>({
      query: ({ teamname, user_id }) => ({
        url: `teams/${teamname}/members`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: [user_id],
      }),
    }),
    removeTeamMember: build.mutation<void, { teamname: string; user_id: Uuid }>(
      {
        query: ({ teamname, user_id }) => ({
          url: `teams/${teamname}/members`,
          method: "DELETE",
          mode: "cors",
          credentials: "include",
          body: [user_id],
        }),
      },
    ),
    sessionUserTeams: build.query<UserTeam[], void>({
      query: () => ({
        url: "user/me/teams",
        mode: "cors",
        credentials: "include",
      }),
    }),

    projectCreate: build.mutation<Project, { username: string; name: string }>({
      query: ({ username, name }) => ({
        url: `projects/${username}`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { name },
      }),
    }),
    project: build.query<Project, { username: string; project: string }>({
      query: ({ username, project }) => ({
        url: `projects/${username}/${project}`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    projects: build.query<Project[], { username: string } & Pagination>({
      query: ({ username }) => ({
        url: `projects/${username}`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    // TODO (b5): fix this mess of overlapping parameters.
    projectUpdate: build.mutation<
      Project,
      {
        username: string
        owner_id: string
        project_name: string
        project_id: string
        name: string
      }
    >({
      query: ({ username, project_name, owner_id, project_id, name }) => ({
        url: `projects/${username}/${project_name}`,
        method: "PUT",
        mode: "cors",
        credentials: "include",
        body: { owner_id, project_id, name },
      }),
    }),
    // TODO (b5): fix this mess of overlapping parameters.
    projectDelete: build.mutation<
      Project,
      { username: string; project: string }
    >({
      query: ({ username, project }) => ({
        url: `projects/${username}/${project}`,
        method: "DELETE",
        mode: "cors",
        credentials: "include",
      }),
    }),
    projectByName: build.query<Project, { username: string; project: string }>({
      query: ({ username, project }) => ({
        url: `projects/${username}/${project}`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    sessionUserProjects: build.query<UserProjects[], void>({
      query: () => ({
        url: "user/me/projects",
        mode: "cors",
        credentials: "include",
      }),
    }),
    projectSettings: build.query<ProjectSettings, UserProjectParams>({
      query: ({ username, project }) => ({
        url: `projects/${username}/${project}/settings`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    projectSettingsUpdate: build.mutation<
      ProjectSettings,
      UserProjectParams & { settings: ProjectSettings }
    >({
      query: ({ username, project, settings }) => ({
        url: `projects/${username}/${project}/settings`,
        method: "PUT",
        mode: "cors",
        credentials: "include",
        body: settings,
      }),
    }),

    userEgress: build.query<EgressStats, { user: string }>({
      query: ({ user }) => ({
        url: `egress/${user}`,
        mode: "cors",
        credentials: "include",
      }),
    }),

    docList: build.query<Document[], UserProjectParams>({
      query: ({ username, project }) => ({
        url: `docs/${username}/${project}`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    docNew: build.mutation<Document, UserProjectParams & { name?: string }>({
      query: ({ username, project, name }) => ({
        url: `docs/${username}/${project}`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { name },
      }),
    }),
    docJoin: build.mutation<Document, UserProjectParams & { ticket: string }>({
      query: ({ username, project, ticket }) => ({
        url: `docs/${username}/${project}/join`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { ticket },
      }),
    }),
    docUpload: build.mutation<
      void,
      { username: string; project: string; docId: string; files: FileList }
    >({
      query: ({ username, project, docId, files }) => {
        const formData = new FormData()
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i])
        }
        return {
          url: `docs/${username}/${project}/${docId}/upload`,
          method: "POST",
          mode: "cors",
          credentials: "include",
          body: formData,
          formData: true,
        }
      },
    }),
    docShare: build.query<
      DocShareResponse,
      UserProjectParams & DocShareRequest
    >({
      query: ({ username, project, docId, mode }) => ({
        url: `docs/${username}/${project}/${docId}/share`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { doc_id: docId, mode },
      }),
    }),
    docGet: build.query<Document, UserProjectParams & { doc_id: string }>({
      query: ({ username, project, doc_id }) => ({
        url: `docs/${username}/${project}/${doc_id}`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    docSet: build.mutation<
      Document,
      UserProjectParams & { doc_id: string; key: string; value: string }
    >({
      query: (params) => ({
        url: `docs/${params.username}/${params.project}/${params.doc_id}/set`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { doc_id: params.doc_id, key: params.key, value: params.value },
      }),
    }),
    docDel: build.mutation<
      DocumentMutationResult,
      UserProjectParams & { doc_id: string; prefix: string }
    >({
      query: ({ username, project, doc_id, prefix }) => ({
        url: `docs/${username}/${project}/${doc_id}/del`,
        method: "DELETE",
        mode: "cors",
        credentials: "include",
        body: { prefix: prefix },
      }),
    }),
    docDrop: build.mutation<
      DocumentMutationResult,
      UserProjectParams & { doc_id: string }
    >({
      query: ({ username, project, doc_id }) => ({
        url: `docs/${username}/${project}/${doc_id}/drop`,
        method: "DELETE",
        mode: "cors",
        credentials: "include",
      }),
    }),

    blobList: build.query<BlobInfo[], UserProjectParams & Pagination>({
      query: ({ username, project, offset, limit }) => ({
        url: `blobs/${username}/${project}?offset=${offset}&limit=${limit}`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    blobUploadTicket: build.mutation<
      void,
      UserProjectParams & UploadBlobRequest
    >({
      query: ({ username, project, tag, ticket }) => ({
        url: `blobs/${username}/${project}`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { tag, ticket },
      }),
    }),
    blobUploadFiles: build.mutation<
      void,
      UserProjectParams & { tag: string; files: FileList }
    >({
      query: ({ username, project, tag, files }) => {
        const formData = new FormData()
        formData.set("tag", tag)
        for (let i = 0; i < files.length; i++) {
          formData.append("files", files[i])
        }
        return {
          url: `blobs/${username}/${project}/upload`,
          method: "POST",
          mode: "cors",
          credentials: "include",
          body: formData,
        }
      },
    }),
    blobsDelete: build.mutation<void, UserProjectParams & { hashes: string[] }>(
      {
        query: ({ username, project, hashes }) => ({
          url: `blobs/${username}/${project}`,
          method: "DELETE",
          mode: "cors",
          credentials: "include",
          body: { hashes },
        }),
      },
    ),
    blobTicket: build.query<string, { hash: string }>({
      query: ({ hash }) => ({
        url: `ticket/${hash}`,
        mode: "cors",
        credentials: "include",
      }),
    }),

    // account & session management
    userSignup: build.mutation<
      User,
      { username: string; name: string; email: string; password: string }
    >({
      query: (credentials) => ({
        url: "user/signup",
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: credentials,
      }),
    }),
    userLogin: build.mutation<
      User,
      { name_or_email: string; password: string }
    >({
      query: (credentials) => ({
        url: "user/login",
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: credentials,
      }),
    }),
    userLogout: build.mutation<User, void>({
      query: () => ({
        url: "user/logout",
        method: "POST",
        mode: "cors",
        credentials: "include",
      }),
    }),
    userForgotPassword: build.mutation<void, { email: string }>({
      query: (body) => ({
        url: "user/forgot",
        method: "POST",
        mode: "cors",
        credentials: "include",
        body,
      }),
    }),
    userResetPassword: build.mutation<
      ResetPasswordResponse,
      ResetPasswordRequest
    >({
      query: (body) => ({
        url: "user/reset",
        method: "POST",
        mode: "cors",
        credentials: "include",
        body,
      }),
    }),
    userVerifyEmail: build.mutation<VerifyEmailResponse, VerifyEmailRequest>({
      query: (body) => ({
        url: "user/verify-email",
        method: "POST",
        mode: "cors",
        credentials: "include",
        body,
      }),
    }),
    userSendVerifyEmail: build.mutation<void, void>({
      query: () => ({
        url: "user/send-verify-email",
        method: "POST",
        mode: "cors",
        credentials: "include",
      }),
    }),
    getSessionUser: build.query<User, void>({
      query: () => ({ url: "user/me", mode: "cors", credentials: "include" }),
    }),
    updateSessionUser: build.mutation<User, { credentials: UserUpdates }>({
      query: ({ credentials }) => ({
        url: "user/me",
        method: "PUT",
        mode: "cors",
        credentials: "include",
        body: credentials,
      }),
    }),

    // billing management
    billingPlanUsage: build.query<BillingPlanUsage, void>({
      query: () => ({
        url: "billing/usage",
        mode: "cors",
        credentials: "include",
      }),
    }),
    invoiceSettings: build.query<StripeInvoiceSettings, { user: string }>({
      query: ({ user }) => ({
        url: `billing/${user}/invoice-settings`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    setDefaultPaymentMethod: build.mutation<
      StripeInvoiceSettings,
      { user: string; paymentMethodId: string }
    >({
      query: ({ user, paymentMethodId }) => ({
        url: `billing/${user}/invoice-settings`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { payment_method_id: paymentMethodId },
      }),
    }),
    listPaymentCards: build.query<StripePaymentMethod, { user: string }>({
      query: ({ user }) => ({
        url: `billing/${user}/cards`,
        method: "POST",
        mode: "cors",
        credentials: "include",
      }),
    }),
    userSubscription: build.query<StripeSubscription, { user: string }>({
      query: ({ user }) => ({
        url: `billing/${user}/subscription`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    promoCodeLookup: build.query<
      { code: string; promo_code_id: string; description: string },
      { code: string }
    >({
      query: ({ code }) => ({
        url: `billing/promo/${code}`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    createSubscription: build.mutation<
      StripeSubscription,
      {
        user: string
        paymentMethodId: string
        baseBillingPeriod: BillingFrequency
        promoCode?: string
      }
    >({
      query: ({ user, paymentMethodId, baseBillingPeriod, promoCode }) => ({
        url: `billing/${user}/subscription`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: {
          payment_method_id: paymentMethodId,
          base_billing_period: baseBillingPeriod,
          promo_code_id: promoCode,
        },
      }),
    }),
    cancelSubscription: build.mutation<
      StripeSubscription,
      { user: string; subscriptionId: string }
    >({
      query: ({ user, subscriptionId }) => ({
        url: `billing/${user}/subscription`,
        method: "DELETE",
        mode: "cors",
        credentials: "include",
        body: { subscription_id: subscriptionId },
      }),
    }),
    upcomingInvoice: build.query<
      StripeInvoice,
      { user: string; subscriptionId: string }
    >({
      query: ({ user, subscriptionId }) => ({
        url: `billing/${user}/upcoming-invoice`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { subscription_id: subscriptionId },
      }),
    }),

    // API Key management
    apiKeyCreate: build.mutation<
      ApiKeyCreateResponse,
      { username: string; name: string }
    >({
      query: ({ username, name }) => ({
        url: `api-keys/${username}`,
        method: "POST",
        mode: "cors",
        credentials: "include",
        body: { name },
      }),
    }),
    apiKeyList: build.query<ApiKey[], { username: string } & Pagination>({
      query: ({ username, offset, limit }) => ({
        url: `api-keys/${username}?offset=${offset}&limit=${limit}`,
        mode: "cors",
        credentials: "include",
      }),
    }),
    apiKeyDelete: build.mutation<void, { username: string; key_id: string }>({
      query: ({ username, key_id }) => ({
        url: `api-keys/${username}/${key_id}`,
        method: "DELETE",
        mode: "cors",
        credentials: "include",
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useNodeStatusQuery,

  useUserQuery,

  useTeamCreateMutation,
  useAddTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useTeamMembersQuery,
  useSessionUserTeamsQuery,

  useProjectCreateMutation,
  useProjectQuery,
  useProjectsQuery,
  useProjectByNameQuery,
  useProjectUpdateMutation,
  useProjectDeleteMutation,
  useSessionUserProjectsQuery,

  useProjectSettingsQuery,
  useProjectSettingsUpdateMutation,

  useUserEgressQuery,

  useDocListQuery,
  useDocNewMutation,
  useDocJoinMutation,
  useDocUploadMutation,
  useDocShareQuery,
  useDocGetQuery,
  useDocSetMutation,
  useDocDelMutation,
  useDocDropMutation,

  useBlobListQuery,
  useBlobTicketQuery,
  useBlobUploadTicketMutation,
  useBlobUploadFilesMutation,
  useBlobsDeleteMutation,

  useUserSignupMutation,
  useUserLoginMutation,
  useUserLogoutMutation,
  useUserForgotPasswordMutation,
  useUserResetPasswordMutation,
  useUserVerifyEmailMutation,
  useUserSendVerifyEmailMutation,
  useGetSessionUserQuery,
  useUpdateSessionUserMutation,

  useBillingPlanUsageQuery,
  useInvoiceSettingsQuery,
  useSetDefaultPaymentMethodMutation,
  useListPaymentCardsQuery,
  useUserSubscriptionQuery,
  usePromoCodeLookupQuery,
  useCreateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useUpcomingInvoiceQuery,

  useApiKeyCreateMutation,
  useApiKeyListQuery,
  useApiKeyDeleteMutation,
} = api

export function getDoc(doc_id: string): Promise<Document> {
  return fetch(`${IROH_ANCHOR_API_PREFIX}docs/${doc_id}`, {
    mode: "cors",
    credentials: "include",
  }).then((data) => data.json())
}

export function fetchDailyEgress(): Promise<EgressStats> {
  return fetch(`${IROH_ANCHOR_API_PREFIX}anchors/egress`, {
    mode: "cors",
    credentials: "include",
  })
    .then((data) => data.json())
    .catch(() => ({
      summary: {
        egress_bytes: 0,
        egress_blobs: 0,
        requests: 0,
      },
      daily: [],
    }))
}

export function getNetworkProviderInfo() {
  return fetch(`${IROH_ANCHOR_API_PREFIX}anchors/provider`, {
    mode: "cors",
    credentials: "include",
  }).then((data) => data.json())
}

export function fetchDocItemByKeyUrl(
  username: string,
  project: string,
  doc_id: string,
  key: string,
) {
  return `${IROH_ANCHOR_API_PREFIX}docs/${username}/${project}/${doc_id}/get/${key}`
}

function isIrohError(err: unknown): IrohError | undefined {
  if (typeof err === "object" && err !== null) {
    if ("code" in err && "error" in err) {
      return err as IrohError
    }
  }
  return undefined
}

// interpret a fetch error into a user-facing string.
export function serverErrorString(
  err: FetchBaseQueryError | SerializedError,
): string {
  if ("status" in err) {
    const irohError = isIrohError(err.data)
    if (irohError) {
      return irohError.error
    }

    // Error is of type FetchBaseQueryError
    if (typeof err.status === "number") {
      // TODO - HTTP status code interpretation, or do we just run with the error message?
      switch (err.status) {
        case 422: // Unprocessable Entity: axum returns this as a raw string
          return err.data as string
      }

      if (typeof err === "string") {
        return err
      }
      if (typeof err.data === "string") {
        // This is the error form returned by the iroh backend API:
        // server side errors have status code numbers
        return err.data as string
      }

      // shrug emoji
      return `An unknown error occurred. Status Code: ${err.status}`
    }

    console.warn(err.status, err.error)

    switch (err.status) {
      case "FETCH_ERROR":
        return "Couldn't connect to the server. Please check your internet connection and try again."
      case "PARSING_ERROR":
        return "The server returned an invalid response. Please try again later."
      case "TIMEOUT_ERROR":
        return "The server took too long to respond. Please try again later."
      case "CUSTOM_ERROR":
        return err.error
    }
  }

  // Error is of type SerializedError
  return err.message || "An unknown error occurred. Please try again later."
}
